export interface PlaybackMapRange {
  from: string;
  to: string;
}

export interface PlaybackMapResponsePayload {
  type: string;
  status: number;
  start: string;
  end: string;
  source: string;
  target: string;
  ranges: PlaybackMapRange[];
}

/**
 * Treat a date picker's calendar fields as camera GMT wall-clock.
 * Camera OSD / playback-map tokens are GMT; users pick the same digits locally.
 */
export function calendarFieldsAsCameraGmtMillis(d: Date): number {
  if (!d || Number.isNaN(d.getTime())) {
    return NaN;
  }
  return Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds()
  );
}

/** Format epoch ms as camera GMT for UI (matches video OSD). */
export function formatCameraGmtDateTime(ms: number, withSeconds = true): string {
  if (!Number.isFinite(ms)) {
    return '';
  }
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  const time = withSeconds
    ? `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    : `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  return `${date} ${time}`;
}

/** Compact tick parts in camera GMT (date = DD/MM, time = HH:mm). */
export function formatCameraGmtTickParts(ms: number): { dateLabel: string; timeLabel: string } {
  if (!Number.isFinite(ms)) {
    return { dateLabel: '', timeLabel: '' };
  }
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    dateLabel: `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}`,
    timeLabel: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  };
}

/** Camera stores recordings in GMT; format instant as `YYYYMMDD-HHmmss` for WebSocket playback-map. */
export function formatDateToPlaybackMapToken(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${day}-${h}${min}${s}`;
}

/** Camera GMT wall-clock `YYYYMMDD-HHmm` for WebSocket playback seek. */
export function formatPlaybackSeekToken(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}${m}${day}-${h}${min}`;
}

/** Parse camera GMT token `YYYYMMDD-HHmmss` to epoch ms. */
export function parsePlaybackMapTokenToMillis(token: string): number {
  const [datePart, timePart] = token.split('-');
  const y = +datePart.slice(0, 4);
  const mo = +datePart.slice(4, 6) - 1;
  const d = +datePart.slice(6, 8);
  const hh = +timePart.slice(0, 2);
  const mm = +timePart.slice(2, 4);
  const ss = timePart.length >= 6 ? +timePart.slice(4, 6) : 0;
  return Date.UTC(y, mo, d, hh, mm, ss);
}

export function formatAlertClipFilenameParts(d: Date): { dateStr: string; timeStr: string } {
  const dateStr =
    `${d.getUTCFullYear()}` +
    `${String(d.getUTCMonth() + 1).padStart(2, '0')}` +
    `${String(d.getUTCDate()).padStart(2, '0')}`;
  const timeStr =
    `${String(d.getUTCHours()).padStart(2, '0')}` +
    `${String(d.getUTCMinutes()).padStart(2, '0')}` +
    `${String(d.getUTCSeconds()).padStart(2, '0')}`;
  return { dateStr, timeStr };
}

export function segmentDurationLabel(r: PlaybackMapRange): string {
  const a = parsePlaybackMapTokenToMillis(r.from);
  const b = parsePlaybackMapTokenToMillis(r.to);
  const sec = Math.max(0, Math.round((b - a) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

/** Camera stores historical video as 1-minute chunks from each map segment start. */
export const PLAYBACK_CHUNK_MS = 60_000;

/**
 * Floor a wall time to the start of the 1-minute chunk inside a playback-map segment.
 * Chunks are anchored at `segmentStartMs`, not at wall-clock minute boundaries.
 */
export function alignSeekToPlaybackChunkStart(
  seekMs: number,
  segmentStartMs: number,
  segmentEndMs?: number
): number {
  if (!Number.isFinite(seekMs) || !Number.isFinite(segmentStartMs)) {
    return seekMs;
  }
  const offset = Math.max(0, seekMs - segmentStartMs);
  const chunkIndex = Math.floor(offset / PLAYBACK_CHUNK_MS);
  let aligned = segmentStartMs + chunkIndex * PLAYBACK_CHUNK_MS;
  if (segmentEndMs != null && Number.isFinite(segmentEndMs) && segmentEndMs > segmentStartMs) {
    const lastChunkStart = segmentStartMs +
      Math.floor((segmentEndMs - segmentStartMs - 1) / PLAYBACK_CHUNK_MS) * PLAYBACK_CHUNK_MS;
    aligned = Math.min(aligned, Math.max(segmentStartMs, lastChunkStart));
  }
  return aligned;
}

/** Recording segment from playback-map that contains `seekMs` (half-open [from, to)). */
export function findPlaybackMapRangeContaining(
  seekMs: number,
  ranges: PlaybackMapRange[]
): PlaybackMapRange | null {
  if (!ranges?.length || !Number.isFinite(seekMs)) {
    return null;
  }
  for (const r of ranges) {
    const startMs = parsePlaybackMapTokenToMillis(r.from);
    const endMs = parsePlaybackMapTokenToMillis(r.to);
    if (seekMs >= startMs && seekMs < endMs) {
      return r;
    }
  }
  return null;
}
